/**
 * The case's expenses: filing fees, premium processing, couriers. Filed
 * receipt notices and the assistant suggest them; anyone on the case adds,
 * confirms or discards; firm admins approve them on Billing review.
 */
import { Check, Pencil, Plus, Receipt, Sparkles, Trash2 } from "lucide-react";
import { useState, type FC, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";

import { expenseActions } from "../actions";
import { useCaseExpenses, useTimeStatus } from "../queries";
import { Segmented } from "../settings/SettingRow";
import type {
  ExpenseKind,
  ExpensePayer,
  NewExpense,
  TimeExpense
} from "../types";
import { formatAmount, todayIso } from "../utils";
import {
  canEditExpense,
  EXPENSE_KINDS,
  EXPENSE_STATUS_LABEL,
  expenseTotal,
  originText,
  parseAmount,
  PAYER_OPTIONS,
  payerText
} from "./expenseModel";

const shortDate = (iso: string): string => {
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

type FormProps = {
  individualId: string;
  companyCase: boolean;
  expense?: TimeExpense;
  trigger: ReactNode;
};

const ExpenseForm: FC<FormProps> = ({
  individualId,
  companyCase,
  expense,
  trigger
}) => {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<ExpenseKind>(expense?.kind ?? "filing_fee");
  const [amount, setAmount] = useState(expense ? String(expense.amount) : "");
  const [date, setDate] = useState(expense?.date ?? todayIso());
  const [description, setDescription] = useState(expense?.description ?? "");
  const [payer, setPayer] = useState<ExpensePayer>(expense?.payer ?? "split");
  const [benefit, setBenefit] = useState(expense?.employee_benefit ?? false);
  const idBase = `expense-${expense?.id ?? "new"}`;
  const value = parseAmount(amount);

  const save = async () => {
    if (value === null) return;
    const body: NewExpense = {
      kind,
      amount: value,
      date,
      description: description.trim()
    };
    if (companyCase) {
      body.payer = payer;
      body.employee_benefit =
        kind === "premium_processing" && payer === "employee" && benefit;
    }
    const out = expense
      ? await expenseActions.update(expense.id, body)
      : await expenseActions.add(individualId, body);
    if (out) {
      setOpen(false);
      if (!expense) {
        setAmount("");
        setDescription("");
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="end" className="flex w-80 flex-col gap-2.5">
        <span className="text-base font-medium">
          {expense ? "Edit expense" : "Add an expense"}
        </span>
        <span className="grid grid-cols-[minmax(0,1fr)_110px] gap-2">
          <Select
            value={kind}
            onValueChange={(next) => setKind(next as ExpenseKind)}
          >
            <SelectTrigger size="sm" aria-label="Kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXPENSE_KINDS.map((k) => (
                <SelectItem key={k.id} value={k.id}>
                  {k.label}{" "}
                  <span className="font-mono text-sm text-fg-3">{k.code}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            aria-label="Amount"
            inputMode="decimal"
            placeholder="$0.00"
            value={amount}
            className="h-8 text-right tabular-nums"
            onChange={(e) => setAmount(e.currentTarget.value)}
          />
        </span>
        <Input
          aria-label="Date"
          type="date"
          value={date}
          className="h-8"
          onChange={(e) => setDate(e.currentTarget.value)}
        />
        <Input
          aria-label="Description"
          placeholder="What it was for (I-129 filing fee)"
          value={description}
          className="h-8"
          onChange={(e) => setDescription(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save();
          }}
        />
        {companyCase ? (
          <span className="flex flex-col gap-1.5">
            <span className="text-sm text-fg-3">Who pays</span>
            <Segmented
              label="Who pays"
              value={payer}
              options={PAYER_OPTIONS}
              onChange={setPayer}
            />
            {kind === "premium_processing" && payer === "employee" ? (
              <span className="flex items-start gap-2">
                <Checkbox
                  id={`${idBase}-benefit`}
                  checked={benefit}
                  onCheckedChange={(next) => setBenefit(next === true)}
                />
                <Label
                  htmlFor={`${idBase}-benefit`}
                  className="text-sm leading-snug font-normal"
                >
                  The employee asked for premium processing for their own
                  benefit
                </Label>
              </span>
            ) : null}
          </span>
        ) : null}
        <Button
          variant="primary"
          size="sm"
          disabled={value === null}
          onClick={() => void save()}
        >
          {expense
            ? "Save"
            : `Add ${value === null ? "" : formatAmount(value)}`}
        </Button>
      </PopoverContent>
    </Popover>
  );
};

const SuggestedRow: FC<{
  expense: TimeExpense;
  individualId: string;
  companyCase: boolean;
}> = ({ expense, individualId, companyCase }) => {
  const Icon = expense.origin === "receipt" ? Receipt : Sparkles;
  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-dashed border-line-2 bg-fill-1 px-3 py-2"
      data-testid="case-expense-suggestion"
    >
      <Icon className="size-4 shrink-0 text-fg-3" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-base">
          <span className="font-medium">{expense.kind_label}</span>
          {expense.description ? ` · ${expense.description}` : ""}
        </span>
        <span className="text-sm text-fg-3">
          {[originText(expense), shortDate(expense.date)]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </span>
      <span className="text-base font-medium tabular-nums">
        {formatAmount(expense.amount)}
      </span>
      <Button
        variant="primary"
        size="xs"
        onClick={() => void expenseActions.confirm([expense.id])}
      >
        <Check /> Confirm
      </Button>
      <ExpenseForm
        individualId={individualId}
        companyCase={companyCase}
        expense={expense}
        trigger={
          <Button size="icon-xs" variant="ghost" aria-label="Edit expense">
            <Pencil />
          </Button>
        }
      />
      <Button
        size="icon-xs"
        variant="ghost"
        aria-label="Discard expense"
        onClick={() => void expenseActions.discard([expense.id])}
      >
        <Trash2 />
      </Button>
    </div>
  );
};

type Props = { individualId: string; companyCase: boolean };

export const CaseExpenses: FC<Props> = ({ individualId, companyCase }) => {
  const query = useCaseExpenses(individualId);
  const { is_admin: isAdmin } = useTimeStatus();
  const rows = query.data ?? [];
  const suggested = rows.filter((x) => x.status === "suggested");
  const listed = rows.filter((x) => x.status !== "suggested");
  const total = expenseTotal(rows);
  const columns = companyCase ? 7 : 6;

  return (
    <div className="flex flex-col gap-2" data-testid="case-expenses">
      <span className="flex items-center gap-2">
        <span className="text-base font-medium">Expenses</span>
        {total ? (
          <span className="text-sm text-fg-3 tabular-nums">
            {formatAmount(total)}
          </span>
        ) : null}
        <span className="flex-1" />
        <ExpenseForm
          individualId={individualId}
          companyCase={companyCase}
          trigger={
            <Button size="xs" variant="secondary">
              <Plus /> Add expense
            </Button>
          }
        />
      </span>
      {suggested.map((x) => (
        <SuggestedRow
          key={x.id}
          expense={x}
          individualId={individualId}
          companyCase={companyCase}
        />
      ))}
      <div className="shrink-0 overflow-hidden rounded-xl border border-line-2">
        <Table className="table-fixed text-base">
          <TableHeader className="bg-fill-1">
            <TableRow>
              <TableHead className="hidden h-8 w-16 text-sm @xl:table-cell">
                Date
              </TableHead>
              <TableHead className="h-8 text-sm">Expense</TableHead>
              <TableHead className="hidden h-8 w-14 text-sm @xl:table-cell">
                Code
              </TableHead>
              {companyCase ? (
                <TableHead className="hidden h-8 w-28 text-sm @xl:table-cell">
                  Who pays
                </TableHead>
              ) : null}
              <TableHead className="h-8 w-20 text-right text-sm @xl:w-24">
                Amount
              </TableHead>
              <TableHead className="h-8 w-24 text-sm">Status</TableHead>
              <TableHead className="h-8 w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {listed.map((x) => (
              <TableRow key={x.id} data-testid="case-expense-row">
                <TableCell className="hidden text-fg-3 tabular-nums @xl:table-cell">
                  {shortDate(x.date)}
                </TableCell>
                <TableCell>
                  <span className="block truncate">
                    {x.description || x.kind_label}
                  </span>
                  <span className="block truncate text-sm text-fg-3 @xl:hidden">
                    {[
                      shortDate(x.date),
                      x.utbms_expense,
                      companyCase ? payerText(x) : ""
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </TableCell>
                <TableCell
                  className="hidden font-mono text-sm text-fg-3 @xl:table-cell"
                  title={x.code_label}
                >
                  {x.utbms_expense}
                </TableCell>
                {companyCase ? (
                  <TableCell className="hidden truncate text-fg-3 @xl:table-cell">
                    {payerText(x)}
                  </TableCell>
                ) : null}
                <TableCell className="text-right font-medium tabular-nums">
                  {formatAmount(x.amount)}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="secondary"
                    className="h-5 text-sm font-normal"
                  >
                    {EXPENSE_STATUS_LABEL[x.status]}
                  </Badge>
                </TableCell>
                <TableCell className="p-1 text-right">
                  {canEditExpense(x, isAdmin) ? (
                    <span className="flex justify-end">
                      <ExpenseForm
                        individualId={individualId}
                        companyCase={companyCase}
                        expense={x}
                        trigger={
                          <Button
                            size="icon-xs"
                            variant="ghost"
                            aria-label="Edit expense"
                          >
                            <Pencil />
                          </Button>
                        }
                      />
                      <Button
                        size="icon-xs"
                        variant="ghost"
                        aria-label="Discard expense"
                        onClick={() => void expenseActions.discard([x.id])}
                      >
                        <Trash2 />
                      </Button>
                    </span>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
            {!listed.length ? (
              <TableRow>
                <TableCell
                  colSpan={columns}
                  className="py-5 text-center text-sm whitespace-normal text-fg-3"
                >
                  Filing fees, couriers and other case costs. Filed receipt
                  notices suggest their fee here.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
